/**
 * Hook for ExtraHorizon authentication operations
 * Wraps SDK authentication methods and manages auth state
 */

import { useCallback, useRef } from "react";

import {
  selectExhState,
  setExhConnected,
  setExhError,
  setExhLoading,
  setExhUser,
  useAppDispatch,
  useAppSelector,
} from "lib/store";

import {
  clearAllStoredTokens,
  clearStoredRefreshToken,
  getExhClient,
  getStoredRefreshToken,
  isExhClientInitialized,
} from "../client";
import { parseExhError } from "../utils";

export const useExhAuth = () => {
  const dispatch = useAppDispatch();
  const exhState = useAppSelector(selectExhState);
  // Use ref instead of state to persist across StrictMode remounts
  const isInitializedRef = useRef(false);
  // Track if auth is currently in progress to prevent concurrent calls
  const authInProgressRef = useRef(false);

  /**
   * Check if user is authenticated in ExtraHorizon
   */
  const isAuthenticated = useCallback((): boolean => {
    if (!isExhClientInitialized()) {
      return false;
    }

    try {
      const client = getExhClient();
      return Boolean(client && exhState.exhUser);
    } catch {
      return false;
    }
  }, [exhState.exhUser]);

  /**
   * Get current ExtraHorizon user
   */
  const getCurrentUser = useCallback(async () => {
    if (!isExhClientInitialized()) {
      const error = parseExhError(new Error("ExtraHorizon client not initialized"));
      dispatch(setExhError(error));
      throw error;
    }

    dispatch(setExhLoading(true));
    dispatch(setExhError(null));

    try {
      const client = getExhClient();

      // Try to fetch user - the SDK will handle authentication internally
      // If tokens exist, this will succeed. If not, we'll get a 401.
      const user = await client.users.me();

      const exhUser = {
        id: user.id,
        email: user.email,
        profile: {
          firstName: user.firstName,
          lastName: user.lastName,
        },
        staffEnlistments: user.staffEnlistments,
      };

      dispatch(setExhUser(exhUser));
      dispatch(setExhConnected(true));
      return exhUser;
    } catch (err) {
      const error = parseExhError(err);
      // Only set error if it's not a 401 (unauthorized) - which is expected when not logged in
      if (!err || (typeof err === "object" && "status" in err && err.status !== 401)) {
        dispatch(setExhError(error));
      }
      dispatch(setExhConnected(false));
      dispatch(setExhLoading(false));
      return null;
    } finally {
      dispatch(setExhLoading(false));
    }
  }, [dispatch]);

  /**
   * Initialize and fetch current user
   * Attempts to authenticate with stored refresh token if available
   */
  const initializeAuth = useCallback(async () => {
    // Prevent multiple simultaneous calls (important for React StrictMode)
    if (isInitializedRef.current || authInProgressRef.current) {
      return;
    }

    // Set both flags to prevent concurrent calls
    isInitializedRef.current = true;
    authInProgressRef.current = true;

    // Set loading state immediately to prevent premature redirects
    dispatch(setExhLoading(true));

    try {
      if (!isExhClientInitialized()) {
        return;
      }

      const client = getExhClient();

      // Check for stored refresh token in localStorage
      const storedRefreshToken = getStoredRefreshToken();

      if (storedRefreshToken) {
        try {
          // The SDK's authenticate method with refreshToken should restore the session
          // This will trigger freshTokensCallback with new tokens if successful
          await client.auth.authenticate({ refreshToken: storedRefreshToken });
        } catch (err: unknown) {
          // If refresh token is invalid/expired, clear it and require re-login
          if (err && typeof err === "object" && "message" in err) {
            const errMsg = (err as Record<string, unknown>).message;
            if (typeof errMsg === "string" && (errMsg.includes("unknown") || errMsg.includes("invalid_grant"))) {
              clearStoredRefreshToken();
              dispatch(setExhLoading(false));
              return;
            }
          }
          // For other errors, clear tokens and require re-login
          clearStoredRefreshToken();
          dispatch(setExhLoading(false));
          return;
        }
      } else {
        dispatch(setExhLoading(false));
        return;
      }

      // Fetch current user - this will populate user data if authentication succeeded
      await getCurrentUser();
    } catch (err) {
      // Log error for debugging purposes
      // eslint-disable-next-line no-console
      console.debug("ExtraHorizon auth initialization error:", err);
      dispatch(setExhLoading(false));
    } finally {
      // Clear the in-progress flag
      authInProgressRef.current = false;
    }
  }, [dispatch, getCurrentUser]);

  /**
   * Logout from ExtraHorizon
   */
  const logout = useCallback(async () => {
    if (!isExhClientInitialized()) {
      return;
    }

    dispatch(setExhLoading(true));

    try {
      const client = getExhClient();
      if (client.auth && typeof client.auth.logout === "function") {
        await client.auth.logout();
      }

      // Clear all stored tokens (refresh, access, expiry)
      clearAllStoredTokens();

      // Reset initialization flag to allow re-initialization
      isInitializedRef.current = false;

      dispatch(setExhConnected(false));
      dispatch(setExhUser(null));
      dispatch(setExhError(null));
    } catch (err) {
      const error = parseExhError(err);
      dispatch(setExhError(error));
    } finally {
      dispatch(setExhLoading(false));
    }
  }, [dispatch]);

  return {
    // State
    isAuthenticated: isAuthenticated(),
    isConnected: exhState.isConnected,
    currentUser: exhState.exhUser,
    loading: exhState.loading,
    error: exhState.error,

    // Methods
    getCurrentUser,
    initializeAuth,
    logout,
  };
};
